/**
 * features/communications/Inbox.tsx
 * ------------------------------------------------------------------
 * THE SCHOOL'S POST, IN THE SCHOOL'S SYSTEM.
 *
 * On 7 September the portal sent 905 fee reminders. Gmail accepted every
 * one, so every screen reported success, and 905 bounces landed in a
 * mailbox nothing here could see. The school had reached 57 families.
 *
 * This is the other half. Bounces arrive tied to the reminder that
 * caused them — the pupil, the invoice, the address that refused it —
 * and replies arrive tied to the family that sent them, answerable
 * without leaving the portal.
 *
 * WHICH IS WHY THE MAILBOX PASSWORD STAYS PUT. The office asked for it
 * so they could answer parents. That same password is the Workspace
 * super-admin for lkcportal.com, the sender of every system email, and
 * one password-reset away from the only portal admin account. Reading
 * and answering the post here means it never has to be handed over.
 *
 * ORDERED BY WHAT NEEDS DOING, not by what arrived last. A mailbox
 * sorted purely by date buries the bounce that means a family has not
 * been chased for a term under this morning's newsletters.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, limit, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Inbox as InboxIcon, MailWarning, MailOpen, Bot, RefreshCw, Send,
  Check, AlertTriangle, User, ExternalLink,
} from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import {
  MAIL_COLLECTIONS, summaryLine, deskOf, DEPARTMENTS, DEPARTMENT_LABEL,
  type InboxMessage, type MailKind, type Department,
} from '@/lib/mailbox';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, AccentButton,
} from '@/features/finance/ui';

type Filter = 'todo' | 'replies' | 'bounces' | 'all';

const KIND_LABEL: Record<MailKind, string> = {
  reply: 'Reply',
  bounce: 'Did not arrive',
  auto: 'Automatic reply',
  other: 'Message',
};

const when = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toISOString().slice(0, 10);
};

export default function Inbox() {
  const { permissions, userData } = useAuth();
  const [rows, setRows] = useState<InboxMessage[] | null>(null);
  const [readErr, setReadErr] = useState('');
  const [lastRun, setLastRun] = useState('');
  const [filter, setFilter] = useState<Filter>('todo');
  /* A bursar wants the bursary's post, not the whole school's. Persisted
     per browser so it does not have to be re-chosen every morning. */
  const [desk, setDesk] = useState<Department | 'all'>(() => {
    try {
      const v = localStorage.getItem('mailbox.desk');
      return v && (DEPARTMENTS as readonly string[]).includes(v) ? (v as Department) : 'all';
    } catch { return 'all'; }
  });
  useEffect(() => { try { localStorage.setItem('mailbox.desk', desk); } catch { /* private mode */ } }, [desk]);
  const [openId, setOpenId] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, MAIL_COLLECTIONS.inbox), orderBy('receivedAt', 'desc'), limit(500)),
    (s) => { setReadErr(''); setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as InboxMessage)); },
    /* A failed read is not an empty inbox. Saying "no post" when the post
       could not be fetched is the same fault this screen exists to fix. */
    (e) => { setReadErr(e.message || 'The inbox could not be read.'); setRows([]); },
  ), []);

  useEffect(() => onSnapshot(doc(db, 'mailSyncState', 'inbox'),
    (s) => setLastRun((s.data()?.lastRunAt as string) ?? ''),
    () => setLastRun('')), []);

  const onDesk = useMemo(
    () => (rows ?? []).filter((r) => desk === 'all' || deskOf(r) === desk),
    [rows, desk],
  );

  const counts = useMemo(() => {
    const all = onDesk;
    return {
      todo: all.filter((r) => !r.handled && r.kind !== 'auto').length,
      bounces: all.filter((r) => r.kind === 'bounce').length,
      replies: all.filter((r) => r.kind === 'reply').length,
      unmatched: all.filter((r) => r.matchedBy === 'none').length,
    };
  }, [onDesk]);

  const shown = useMemo(() => {
    const all = onDesk;
    const list = filter === 'todo' ? all.filter((r) => !r.handled && r.kind !== 'auto')
      : filter === 'replies' ? all.filter((r) => r.kind === 'reply')
        : filter === 'bounces' ? all.filter((r) => r.kind === 'bounce')
          : all;
    /* Unanswered first, then newest. A mailbox sorted only by date buries
       the thing that still needs doing. */
    return [...list].sort((a, b) => Number(Boolean(a.handled)) - Number(Boolean(b.handled))
      || (b.receivedAt ?? '').localeCompare(a.receivedAt ?? ''));
  }, [onDesk, filter]);

  const open = shown.find((r) => r.id === openId) ?? null;

  async function checkNow() {
    setBusy(true); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'syncMailNow')({}) as
        { data: { fetched: number; bounces?: number; replies?: number; skipped?: boolean } };
      setMsg(r.data.skipped
        ? { ok: false, text: 'Email is not configured, so there was nothing to collect.' }
        : {
          ok: true,
          text: r.data.fetched === 0
            ? 'Nothing new.'
            : `${r.data.fetched} new — ${r.data.bounces ?? 0} did not arrive, ${r.data.replies ?? 0} replies.`,
        });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function send() {
    if (!open) return;
    setBusy(true); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'replyToMail')({ id: open.id, body: reply }) as
        { data: { to: string } };
      setReply('');
      setMsg({ ok: true, text: `Sent to ${r.data.to}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function moveTo(m: InboxMessage, department: Department) {
    setBusy(true); setMsg(null);
    try {
      await httpsCallable(functions, 'reassignMail')({ id: m.id, department });
      setMsg({ ok: true, text: `Moved to ${DEPARTMENT_LABEL[department]}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function markHandled(m: InboxMessage) {
    setBusy(true);
    try {
      await httpsCallable(functions, 'markMailHandled')({ id: m.id, handled: !m.handled });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  if (!permissions.canReadSchoolMail) {
    return (
      <div className="p-6">
        <SoftCard>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The school mailbox is not part of your role. It carries every family's
            correspondence, so it is kept to the office and the bursary.
          </p>
        </SoftCard>
      </div>
    );
  }

  if (rows === null) return <div className="p-6 flex justify-center"><Spinner label="Loading…" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={InboxIcon} tone="navy"
        title="School mailbox"
        subtitle={lastRun ? `Post collected ${when(lastRun)}` : 'Replies from parents, and everything that did not arrive'}
        actions={(
          <AccentButton onClick={checkNow} disabled={busy}>
            <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Check now
          </AccentButton>
        )}
      />

      {readErr && (
        <SoftCard className="mb-4 border-l-4 border-red-500">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            The inbox could not be read — this is not an empty inbox.
          </p>
          <p className="text-[12px] text-gray-500 mt-1">{readErr}</p>
        </SoftCard>
      )}

      {msg && (
        <SoftCard className={`mb-4 border-l-4 ${msg.ok ? 'border-emerald-500' : 'border-red-500'}`}>
          <p className={`text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.text}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatChip tone={counts.todo ? 'amber' : 'emerald'} icon={MailOpen}
          value={String(counts.todo)} label="Still to deal with" />
        <StatChip tone={counts.bounces ? 'rose' : 'slate'} icon={MailWarning}
          value={String(counts.bounces)} label="Did not arrive" />
        <StatChip tone="navy" icon={InboxIcon} value={String(counts.replies)} label="Replies" />
        <StatChip tone={counts.unmatched ? 'amber' : 'slate'} icon={User}
          value={String(counts.unmatched)} label="No pupil matched" />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <select value={desk} onChange={(e) => { setDesk(e.target.value as Department | 'all'); setOpenId(''); }}
          className={glassFieldClass('max-w-[180px] text-xs mr-1')}>
          <option value="all">Every desk</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>{DEPARTMENT_LABEL[d]}</option>
          ))}
        </select>
        {([
          ['todo', `To deal with (${counts.todo})`],
          ['replies', `Replies (${counts.replies})`],
          ['bounces', `Did not arrive (${counts.bounces})`],
          ['all', `Everything (${(rows ?? []).length})`],
        ] as [Filter, string][]).map(([id, label]) => (
          <button key={id} onClick={() => { setFilter(id); setOpenId(''); }}
            className={`text-xs px-3 py-1.5 rounded-lg border ${
              filter === id
                ? 'bg-[#1b3a6b] text-white border-[#1b3a6b]'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SoftCard pad="p-0" className="lg:col-span-2">
          <PanelHeader icon={InboxIcon} tone="navy" title="Post"
            subtitle="Unanswered first" />
          {shown.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">
              {(rows ?? []).length === 0
                ? 'Nothing has been collected yet. Press Check now.'
                : 'Nothing in this list.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[70vh] overflow-y-auto">
              {shown.map((m) => (
                <button key={m.id} onClick={() => { setOpenId(m.id); setReply(''); setMsg(null); }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                    openId === m.id ? 'bg-gray-50 dark:bg-gray-800/60' : ''
                  } ${m.handled ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    {m.kind === 'bounce' ? <MailWarning size={14} className="text-red-500 mt-0.5 shrink-0" />
                      : m.kind === 'auto' ? <Bot size={14} className="text-gray-400 mt-0.5 shrink-0" />
                        : <MailOpen size={14} className="text-primaryLight mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {m.studentName || m.fromName || m.from || '(unknown sender)'}
                      </p>
                      <p className="text-[12px] text-gray-500 truncate">{m.subject}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {KIND_LABEL[m.kind]} · {DEPARTMENT_LABEL[deskOf(m)]} · {when(m.receivedAt)}
                        {m.handled && ' · dealt with'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SoftCard>

        <SoftCard pad="p-0" className="lg:col-span-3">
          {!open ? (
            <p className="p-10 text-sm text-gray-500 text-center">Choose something to read.</p>
          ) : (
            <>
              <PanelHeader
                icon={open.kind === 'bounce' ? MailWarning : MailOpen}
                tone={open.kind === 'bounce' ? 'gold' : 'navy'}
                title={open.subject}
                subtitle={`${open.fromName ? `${open.fromName} · ` : ''}${open.from} · ${when(open.receivedAt)}`}
                right={(
                  <button onClick={() => markHandled(open)} disabled={busy}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <Check size={12} className="inline mr-1" />
                    {open.handled ? 'Not dealt with' : 'Dealt with'}
                  </button>
                )}
              />
              <div className="p-4">
                <p className={`text-sm font-medium mb-3 ${
                  open.kind === 'bounce' ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-gray-200'
                }`}>
                  {summaryLine(open)}
                </p>

                {(open.studentName || open.invoiceNo) && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {open.studentName && (
                      <a href={`#/studentProfile/${open.studentId}`}
                        className="text-[12px] px-2 py-0.5 rounded bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200 hover:underline">
                        {open.studentName} <ExternalLink size={9} className="inline -mt-0.5" />
                      </a>
                    )}
                    {open.invoiceNo && (
                      <span className="text-[12px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                        {open.invoiceNo}
                      </span>
                    )}
                    {/* An address match is a guess. Said so, rather than
                        presented with the same confidence as a threaded one. */}
                    {open.matchedBy === 'address' && (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                        matched by email address, not by the message itself
                      </span>
                    )}
                  </div>
                )}

                {/* Read off the alias it was delivered to, and movable —
                    the envelope is right far more often than not, and when
                    it is wrong a person should be able to say so. */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] text-gray-400">Desk</span>
                  <select
                    value={deskOf(open)}
                    onChange={(e) => moveTo(open, e.target.value as Department)}
                    disabled={busy}
                    className={glassFieldClass('max-w-[170px] text-xs')}
                  >
                    {DEPARTMENTS.map((d) => (
                      <option key={d} value={d}>{DEPARTMENT_LABEL[d]}</option>
                    ))}
                  </select>
                  {open.departmentOverride && (
                    <span className="text-[11px] text-gray-400">
                      moved here by hand
                    </span>
                  )}
                </div>

                {open.bounce?.diagnostic && (
                  <p className="text-[12px] font-mono text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded p-2 mb-3 break-words">
                    {open.bounce.diagnostic}
                  </p>
                )}

                <pre className="text-[13px] whitespace-pre-wrap font-sans text-gray-700 dark:text-gray-200 max-h-[38vh] overflow-y-auto border-t border-gray-100 dark:border-gray-800 pt-3">
                  {open.body || '(no text)'}
                </pre>

                {open.repliedAt && (
                  <p className="mt-3 text-[12px] text-emerald-700 dark:text-emerald-300">
                    Answered {when(open.repliedAt)} by {open.handledBy === userData?.uid ? 'you' : 'the office'}.
                  </p>
                )}

                {/* Replying to a mailer-daemon writes to a machine. Offered
                    only where somebody will read it. */}
                {open.kind === 'bounce' ? (
                  <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 p-3">
                    <p className="text-[12px] text-amber-900 dark:text-amber-200">
                      <AlertTriangle size={13} className="inline mr-1 -mt-0.5" />
                      This is an automatic report from a mail server, not a person — there is
                      nobody to reply to. The family needs a working address before anything
                      reaches them.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-3">
                    <textarea
                      value={reply} onChange={(e) => setReply(e.target.value)}
                      rows={5} maxLength={10000}
                      placeholder={`Reply to ${open.from}…`}
                      className={glassFieldClass('w-full text-sm')}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-gray-400">
                        Goes to {open.from}, in the same conversation.
                      </span>
                      <AccentButton onClick={send} disabled={busy || reply.trim().length < 2}>
                        <Send size={14} /> {busy ? 'Sending…' : 'Send reply'}
                      </AccentButton>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SoftCard>
      </div>
    </div>
  );
}
